import React from 'react';
import { cn } from './cn';

// Shared input styling for Input / Select / Textarea. Width is intentionally
// NOT set here so the compact filter controls can stay auto-width; the form
// primitives below opt into `w-full`.
export const controlClasses =
  'rounded-xl bg-surface text-foreground border border-border px-3.5 py-2.5 text-sm ' +
  'placeholder:text-muted-2 shadow-sm transition-colors ' +
  'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

// Wraps a label (+ optional required mark) around a control.
export const FieldLabel = ({ label, required, htmlFor, children, className }) => (
  <div className={cn('flex flex-col gap-1.5', className)}>
    {label && (
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-status-canceled"> *</span>}
      </label>
    )}
    {children}
  </div>
);

export const Input = React.forwardRef(function Input(
  { label, required, className, id, ...props },
  ref
) {
  const control = (
    <input ref={ref} id={id} className={cn(controlClasses, 'w-full', className)} {...props} />
  );
  if (!label) return control;
  return (
    <FieldLabel label={label} required={required} htmlFor={id}>
      {control}
    </FieldLabel>
  );
});

export const Textarea = React.forwardRef(function Textarea(
  { label, required, className, id, ...props },
  ref
) {
  const control = (
    <textarea
      ref={ref}
      id={id}
      className={cn(controlClasses, 'w-full resize-y min-h-[80px]', className)}
      {...props}
    />
  );
  if (!label) return control;
  return (
    <FieldLabel label={label} required={required} htmlFor={id}>
      {control}
    </FieldLabel>
  );
});

export const Select = React.forwardRef(function Select(
  { label, required, className, id, children, ...props },
  ref
) {
  const control = (
    <select
      ref={ref}
      id={id}
      className={cn(controlClasses, 'w-full pr-8 appearance-none cursor-pointer', className)}
      {...props}
    >
      {children}
    </select>
  );
  if (!label) return control;
  return (
    <FieldLabel label={label} required={required} htmlFor={id}>
      {control}
    </FieldLabel>
  );
});
